<template>
  <div class="device-selection">
    <el-select
      :class="['camera-selection', { 'device-selection-error': !isLessonEnded && hasCameraError }]"
      v-model="deviceCamera"
      :placeholder="$t(cameraSelectPlaceHolder, { device: $t('deviceErrorGuide.camera') })"
      :disabled="disableCameraSelection"
      popper-class="device__sub-select"
    >
      <el-option
        v-for="(item, idx) in cameraOptions"
        :key="idx"
        :label="item.label"
        :value="item.label"
      >
        <div>
          {{ item.label }}
          <div class="defaultDevice" v-if="item.isDefault">{{ $t('systemDefault') }}</div>
        </div>
      </el-option>
    </el-select>

    <el-select
      :class="['mic-selection', { 'device-selection-error': !isLessonEnded &&  hasMicError }]"
      v-model="deviceMic"
      :placeholder="$t(micSelectPlaceHolder, { device: $t('deviceErrorGuide.mic') })"
      :disabled="disableMicSelection"
      popper-class="device__sub-select"
    >
      <el-option
        v-for="(item, idx) in micOptions"
        :key="idx"
        :label="item.label"
        :value="item.label"
      >
        <div>
          {{ item.label }}
          <div class="defaultDevice" v-if="item.isDefault">{{ $t('systemDefault') }}</div>
        </div>
      </el-option>
    </el-select>

    <el-select
      v-if="isIpadDevice || isMobileDevice"
      value=""
      class="speaker-selection"
      placeholder="Same device as the Microphone"
      :disabled="true"
      popper-class="device__sub-select"
    >
    </el-select>

    <el-select
      v-else
      class="speaker-selection"
      v-model="deviceSpeaker"
      :placeholder="$t(speakerSelectPlaceHolder)"
      :disabled="disableSpeakerSelection"
      popper-class="device__sub-select"
    >
      <el-option
        v-for="speaker in speakerOptions"
        :key="speaker.deviceId"
        :label="speaker.label"
        :value="speaker.label"
      >
        <div>
          {{ speaker.label }}
          <div class="defaultDevice" v-if="speaker.isDefault">{{ $t('systemDefault') }}</div>
        </div>
      </el-option>
    </el-select>

  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { debounce, store } from '@/utils/common';
import deviceMixin from '@/mixins/device';
import deviceChangeEvent from '@/utils/deviceChangeEvent';

export default {
  mixins: [deviceMixin],
  data() {
    return {
      debounceDeviceChange: null,
      deviceChangeStop: null,
      notificationsPaused: false,
      lastDeviceList: []
    };
  },
  async mounted() {
    this.lastDeviceList = await navigator.mediaDevices.enumerateDevices();

    if (this.localUser.stream) {
      await this.updateDeviceList();
      this.setActiveDevicesOnStream();
    }
  },
  watch: {
    'localUser.stream': {
      handler: async function (value, oldValue) {
        if (!oldValue) {
          this.deviceChangeListener();
          await this.updateDeviceList();
          this.setActiveDevicesOnStream();
        }
      }
    },
    'rtcClient.remoteUsers': {
      handler: async function (val) {
        if (val?.length) {
          await this.attachToSpeaker();
        }
      }
    },
    isShareScreenOn(state) {
      this.changeState(state);
    },
    async selectedSpeaker(newVal, oldVal) {
      if (oldVal === newVal || !oldVal) {
        return;
      }

      const speaker = this.speakers.find(speaker => speaker.label === newVal);
      if (speaker) {
        store.setSpeakerId(this.userId, speaker.deviceId);
        await this.attachToSpeaker();
      }
    }
  },
  computed: {
    ...mapState(['provider', 'lessonId', 'userId']),
    ...mapState('videoRtc', ['rtcClient', 'localUser', 'isShareScreenOn', 'virtualBg']),
    ...mapState('devices', [
      'mics',
      'cameras',
      'selectedMic',
      'selectedCamera',
      'micEnabled',
      'micSelection',
      'videoSelection',
      'speakers',
      'selectedSpeaker'
    ]),
    ...mapState('lesson', ['isLessonBeyondGracePeriod']),
    ...mapState('socketio', ['voluntaryLessonTermination']),
    ...mapState('layout', ['isIpadDevice']),
    ...mapGetters('devices', [
      'mic', 
      'camera', 
      'speaker', 
      'cameraSelectPlaceHolder', 
      'micSelectPlaceHolder',
      'speakerSelectPlaceHolder',
      'hasCameraError',
      'hasMicError',
      'isCameraPermissionDenied',
      'isMicPermissionDenied'
    ]),
    ...mapGetters('uaParser', [
      'isMobileDevice',
      'isMobileDeviceAndNotInAllowedList',
      'isSafariBrowser',
      'isFirefoxBrowser',
      'isMobileAndroid'
    ]),
    ...mapGetters(['isLessonEnded', 'isWaitingForLesson', 'isAmazonChime']),

    cameraOptions() {
      const options = [
        {
          deviceId: 'default',
          label: 'Unable to access camera'
        }
      ];
      return this.cameras.length ? this.cameras : options;
    },

    micOptions() {
      const options = [
        {
          deviceId: 'default',
          label: 'Unable to access mic'
        }
      ];
      return this.mics.length ? this.mics : options;
    },

    speakerOptions() {
      const options = [
        {
          deviceId: 'default',
          label: 'Unable to access speaker'
        }
      ];

      return this.speakers.length ? this.speakers : options;
    },

    deviceMic: {
      // getter
      get: function() {
        return this.hasMicError ? null : this.selectedMic;
      },
      // setter
      set: async function(newValue) {
        if (this.selectedMic === newValue) return;
        const { deviceId } = this.mics.find(key => key.label === newValue);
        const rtc = await this.rtcClient.getAdapter().setAudioDevice(deviceId);
        this.emitUpdatedStream(rtc, 'audio', newValue, deviceId);
        if (!rtc.error) {
          this.showDeviceNotification('changeDevice', newValue);
        } else {
          this.showDeviceNotification('deviceError');
        }
      }
    },

    deviceCamera: {
      // getter
      get: function() {
        return this.hasCameraError ? null : this.selectedCamera;
      },
      // setter
      set: async function(newValue) {
        if (this.selectedCamera === newValue) return;
        const { deviceId } = this.cameras.find(key => key.label === newValue);
        const rtc = await this.rtcClient.getAdapter().setVideoDevice(deviceId);
        this.emitUpdatedStream(rtc, 'video', newValue, deviceId);
        if (!rtc.error) {
          this.showDeviceNotification('changeDevice', newValue);
        } else {
          this.showDeviceNotification('deviceError');
        }
      }
    },

    deviceSpeaker: {
      // getter
      get: function() {
        return this.selectedSpeaker;
      },
      // setter
      set: async function(newValue) {
        const { label, deviceId } = this.speakers.find(key => key.label === newValue);
        this.selectSpeaker(label);
        store.setSpeakerId(this.userId, deviceId, true);

        this.$nextTick(() => {
          this.showDeviceNotification('changeDevice', label);
        });
      }
    },

    activeAudioTrack() {
      return this.localUser.stream.getAudioTracks()[0];
    },

    activeAudioTrackLabel() {
      return this.activeAudioTrack?.label.replace('Default - ', '');
    },

    activeVideoTrack() {
      return this.localUser.stream.getVideoTracks()[0];
    },

    // When initial set audio is not on the list, select
    // first item on the PC - default device
    // last item on the SP - external device
    fallbackMicrophone() {
      return this.isMobileDevice ? this.mics?.slice(-1)[0].label : this.mics?.[0].label;
    },

    disableMicSelection() {
      return this.micSelection === false 
      || this.isLessonEnded 
      || this.isMobileDeviceAndNotInAllowedList 
      || this.isWaitingForLesson
      || this.isMicPermissionDenied;
    },

    disableCameraSelection() {
      return this.videoSelection === false 
      || this.isLessonEnded 
      || this.isMobileDeviceAndNotInAllowedList 
      || this.isMobileDevice
      || this.isWaitingForLesson
      || this.isCameraPermissionDenied;
    },
    
    disableSpeakerSelection() {
      return this.isLessonEnded 
      || !this.speakers.length 
      || this.isWaitingForLesson 
      || this.isMicPermissionDenied;
    },
  },
  methods: {
    ...mapActions('devices', [
      'getMics',
      'getCameras',
      'getSpeakers',
      'selectCamera',
      'selectMic',
      'selectSpeaker',
      'setMediaDeviceError'
    ]),
    ...mapActions('videoRtc', ['updateLocalStream']),
    ...mapMutations('devices', [
      'switchVideoSelection',
      'switchMicSelection'
    ]),
    ...mapMutations('videoRtc', ['setActiveAudioTrack']),

    showDeviceNotification(type, device) {
      let message = this.$t('deviceNotification.info');
      let messageType = "info"
      const android = this.isMobileAndroid && device.includes('Bluetooth') ? 'Android' : '';

      switch (type) {
        case "changeDevice":
          message = this.$t(`deviceNotification.success${android}`, [device]);
          messageType = "success";
          break;
        case "deviceError":
          message = this.$t('deviceNotification.warning');
          messageType = "warning";
          break;
      }

      if (this.notificationsPaused === false) {
        const randomNum = Math.floor(Math.random() * 60000);
        const randomId = `notifCheckbox${randomNum}`;
        message += `<hr><input
          class="notif-checkbox"
          id="${randomId}"
          type="checkbox"
          /><label for="${randomId}">${this.$t('dontShowDeviceNotificationAgainMessage')}</label>
          `;
        this.notifyTrigger(message, messageType);
      }
    },

    // trigger notification pop-up
    notifyTrigger(message,messageType){
      this.$notify({
        title: 'Device Update',
        message: message,
        dangerouslyUseHTMLString: true,
        type: messageType,
        position: 'bottom-right',
        duration: 3000,
        customClass: 'device-notification',
        onClose: () => {
          const checkedCheckbox = document.querySelector('.notif-checkbox:checked');
          if (checkedCheckbox) {
            this.notificationsPaused = true;
          } else {
            this.notificationsPaused = false;
          }
        }
      })
    },
    // Set the active/selected device on the stream
    setActiveDevicesOnStream() {
      const defaultCamera = this.activeVideoTrack && this.virtualBg === 'none'
        ? this.activeVideoTrack.label
        : this.selectedCamera;
      
      if (this.cameras.length) {
        // check if the selected camera as label exists
        const cameraLabelExist = this.cameras.find(key => key.label === defaultCamera);
        let selectedCamera = defaultCamera;
        
        // check maybe selectedCamera is using deviceId instead of label
        // this due to device setting page is using a deviceId instead of label
        if (!cameraLabelExist) {
          const cameraIdExist = this.cameras.find(key => key.deviceId === defaultCamera);
          selectedCamera = cameraIdExist ? cameraIdExist.label : this.cameras[0].label;
        } 

        this.selectCamera(selectedCamera);
      } else {
        this.selectCamera(null);
      }

      // Get the active audio track label
      if (this.mics.length) {
        const miclabelExist = this.mics.find(key => key.label === this.activeAudioTrackLabel);
        this.selectMic(miclabelExist ? this.activeAudioTrackLabel : this.fallbackMicrophone);
      } else {
        this.selectMic('No audio found');
      }

      // store selected speaker
      if (this.speakers.length) {
        const speakerId = store.getSpeakerId(this.userId);
        const activeSpeaker = this.speakers.find(speaker => speaker.deviceId === speakerId);
        this.selectSpeaker(activeSpeaker?.label ?? this.speakers[0].label);

        store.setSpeakerId(this.userId, activeSpeaker?.deviceId);
      }
    },

    // Updates the devices' selection
    async updateDeviceList() {
      await this.getMics();
      await this.getCameras();
      await this.getSpeakers();
    },

    // Media devices have changed (plugged in/out device)
    async deviceChanges() {
      const currentDevices = await navigator.mediaDevices.enumerateDevices();

      if (this.isDeviceListUnchanged(this.lastDeviceList, currentDevices)) {
        return;
      }
      this.lastDeviceList = currentDevices;

      await this.updateDeviceList();

      const rtc = await this.rtcClient.getAdapter().updateDeviceStream();
      if (rtc) {
        this.updateLocalStream(rtc);
        this.showDeviceNotification();
      }

      this.setActiveDevicesOnStream();
    },

    // emit updated stream on the parent `video` component
    emitUpdatedStream(rtc, device, newValue, deviceId) {
      if (device === 'video') {
        store.setCameraId(this.userId, deviceId, true);
        this.selectCamera(newValue);
      }

      if (device === 'audio') {
        store.setMicId(this.userId, deviceId, true);
        this.selectMic(newValue);
        this.setActiveAudioTrack(this.localUser.stream.getAudioTracks());
      }

      if (rtc && !rtc.hasOwnProperty('error')) {
        this.updateLocalStream(rtc);
        this.setMediaDeviceError({ device });
      } else {
        this.$logger.error('Error on selecting ' + device, {
          operation: `devices:${device}-select`,
          payload: `${device} name: ${newValue}`,
          event_type: 5,
          lesson_id: this.lessonId
        });
      }
    },

    // Listen for changes to media devices and update the list accordingly
    deviceChangeListener() {
      this.debounceDeviceChange = debounce(this.deviceChanges, 1200);
      this.deviceChangeStop = deviceChangeEvent(this.debounceDeviceChange);
    },

    // Update device selection state based on screenshare state and browser
    changeState(mode = false) {
      const isBrowserSupport = this.isSafariBrowser || this.isFirefoxBrowser;
      if (mode && isBrowserSupport) {
        this.switchVideoSelection(false);
        this.switchMicSelection(false);
        return;
      }
      if (mode) {
        this.switchVideoSelection(false);
        return;
      }
      this.switchVideoSelection(true);
      this.switchMicSelection(true);
    },

    async attachToSpeaker() {
      if (!this.speaker) {
        return;
      }

      let videoElement = document.getElementById('remoteVideo');
      if (videoElement) {
        try {
          await this.attachSinkId(videoElement, this.speaker.deviceId)
        } catch (error) {
            console.error('Error attaching sink ID in Agora:', error);
        }
      }

      if (this.isAmazonChime) {
        try {
          await this.rtcClient.getAdapter().setAudioOutputDevice(this.speaker.deviceId)
        } catch (error) {
            console.error('Error attaching sink ID in Amazon Chime:', error);
        }
      }
    }
  },
  // beforeDestroy
  beforeDestroy: function () {
    this.deviceChangeStop && this.deviceChangeStop();
  }
};
</script>

<style scoped src="@/assets/css/pc/device_selection.css"></style>
